import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { Preferences } from '@capacitor/preferences'

import {
  DEFAULT_PATIENT_SETTINGS,
  fetchPatientSettings,
  updatePatientSettings,
  type PatientSettings,
} from '@/lib/api'

const CACHE_KEY = 'patient_settings'

/**
 * Patient-global preferences (item randomization on repeat, help-grid side,
 * speech retries).
 *
 * Server is canonical (`/api/patient/settings`); the device cache exists so
 * the settings screen and TrainingView can render immediately on cold boot
 * without waiting on the network. Hydration races are fine — the latest
 * server reply wins and is written back to the cache.
 *
 * Lifecycle:
 *   1. `hydrate()` runs once at app mount (from App.vue) and once more right
 *      after first-launch bootstrap (from LaunchTerms). Concurrent calls
 *      share the same inflight promise.
 *   2. Hydration reads the Capacitor Preferences cache for instant paint,
 *      then refreshes from the server when a patient token is present.
 *   3. `save()` is optimistic: it updates the in-memory ref + cache first,
 *      then PUTs. If the PUT fails the caller surfaces the error; we keep
 *      the optimistic value (the user can retry — there is no rollback UX).
 */
export const usePatientSettingsStore = defineStore('patient-settings', () => {
  const settings = ref<PatientSettings>({ ...DEFAULT_PATIENT_SETTINGS })
  const status = ref<'idle' | 'hydrating' | 'ready' | 'error'>('idle')
  const lastError = ref<string | null>(null)

  let inflight: Promise<void> | null = null

  const itemOrderOnRepeat = computed(() => settings.value.item_order_on_repeat)
  const helpPosition = computed(() => settings.value.help_position)
  const speechRetries = computed(() => settings.value.speech_retries)

  async function readCache(): Promise<PatientSettings | null> {
    try {
      const { value } = await Preferences.get({ key: CACHE_KEY })
      if (!value) return null
      const parsed = JSON.parse(value) as Partial<PatientSettings>
      return { ...DEFAULT_PATIENT_SETTINGS, ...parsed }
    } catch {
      return null
    }
  }

  async function writeCache(next: PatientSettings): Promise<void> {
    try {
      await Preferences.set({ key: CACHE_KEY, value: JSON.stringify(next) })
    } catch {
      // Cache writes are best-effort; the server is still canonical.
    }
  }

  async function hydrate(): Promise<void> {
    if (inflight) return inflight
    inflight = (async () => {
      status.value = 'hydrating'
      const cached = await readCache()
      if (cached) settings.value = cached

      try {
        const server = await fetchPatientSettings()
        settings.value = server
        await writeCache(server)
        status.value = 'ready'
        lastError.value = null
      } catch (e) {
        // No token yet, or offline. Cached/default values stay live; we'll
        // retry on the next hydrate call (e.g. post-bootstrap).
        lastError.value = e instanceof Error ? e.message : String(e)
        status.value = cached ? 'ready' : 'error'
      } finally {
        inflight = null
      }
    })()
    return inflight
  }

  async function save(next: PatientSettings): Promise<void> {
    settings.value = next
    await writeCache(next)
    const server = await updatePatientSettings(next)
    settings.value = server
    await writeCache(server)
  }

  /**
   * Forget everything this device knows about the patient's preferences.
   *
   * Only caller today is a completed device transfer: the account on this
   * device has changed, so the cached blob describes somebody the device is no
   * longer. Resets to defaults rather than leaving the old values on screen
   * until the refetch lands, because the wrong help-grid side is immediately
   * visible and the window is a whole network round-trip.
   */
  async function reset(): Promise<void> {
    settings.value = { ...DEFAULT_PATIENT_SETTINGS }
    status.value = 'idle'
    lastError.value = null
    inflight = null
    try {
      await Preferences.remove({ key: CACHE_KEY })
    } catch {
      // Best-effort, same as every other write to this key.
    }
  }

  return {
    // state
    settings,
    status,
    lastError,
    // getters
    itemOrderOnRepeat,
    helpPosition,
    speechRetries,
    // actions
    hydrate,
    save,
    reset,
  }
})
